import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import type { FieldHelpContent } from "@/components/ui/field-help";
import { PersonAvatar } from "@/modules/shell/PersonAvatar";

export type ActiveFilterChipAvatar = {
  /** Display name — initials (or real photo when `src` is set). */
  label: string;
  /** Real photo URL only — no generated colorful faces. */
  src?: string | null;
};

export type ActiveFilterChipChoice = {
  value: string;
  label: string;
};

export type ActiveFilterChip = {
  key: string;
  label: string;
  onClear: () => void;
  /** People / party initials before the label (assignee, customer, …). */
  avatars?: ActiveFilterChipAvatar[];
  /**
   * Discrete values — label click opens a picker to **change** the filter
   * (most requested). × still clears. Prefer over tip-only.
   */
  choices?: ActiveFilterChipChoice[];
  /** Current value when `choices` is set. */
  value?: string;
  onChange?: (value: string) => void;
  /**
   * Custom edit body (party search). Label click opens the popover with this.
   * Prefer over tip-only; × still clears.
   */
  editPanel?: (close: () => void) => ReactNode;
  /**
   * Side-effect edit without a popover (rare). Ignored when `editPanel` / `choices` set.
   */
  onEdit?: () => void;
  /** Tip under “About” in the edit menu, or label click when no edit. */
  help?: FieldHelpContent;
  /** Optional link inside the tip (e.g. change threshold in Settings). */
  helpLink?: { to: string; label: string };
};

/**
 * Removable active-filter pills — plan/30+35.
 * Label = what’s on → click **changes** value when choices/onEdit wired;
 * only × clears.
 */
export function ActiveFilterChips(props: {
  chips: ActiveFilterChip[];
  className?: string;
}) {
  if (props.chips.length === 0) return null;
  return (
    <div
      className={cn(
        "order-20 flex w-full basis-full flex-wrap items-center gap-1.5 px-1 pb-0.5 pt-1",
        props.className,
      )}
      aria-label="Active filters"
    >
      {props.chips.map((chip) => (
        <ActiveFilterChipPill key={chip.key} chip={chip} />
      ))}
    </div>
  );
}

function defaultChipHelp(label: string): FieldHelpContent {
  return {
    title: label,
    summary: `This filter is on: ${label}. Click the label to change it when options are available. Use × to clear.`,
  };
}

function ActiveFilterChipPill({ chip }: { chip: ActiveFilterChip }) {
  const tipId = useId();
  const labelRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"edit" | "help">("edit");
  const [expanded, setExpanded] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const help = chip.help ?? defaultChipHelp(chip.label);
  const hasMore =
    Boolean(help.detail?.trim()) ||
    Boolean(help.useCases && help.useCases.length > 0);
  const hasChoices = Boolean(chip.choices && chip.choices.length > 0 && chip.onChange);
  const hasEditPanel = Boolean(chip.editPanel);
  const canEdit = hasChoices || hasEditPanel || Boolean(chip.onEdit);
  const canOpen = canEdit || Boolean(chip.help);
  const choices = hasChoices ? chip.choices! : [];

  function placePopover() {
    const el = labelRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      const width = 288;
      setPos({
        top: rect.bottom + 6,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      });
    }
  }

  function openEdit() {
    if (chip.onEdit && !hasChoices && !hasEditPanel) {
      chip.onEdit();
      return;
    }
    placePopover();
    const idx = Math.max(
      0,
      choices.findIndex((c) => c.value === chip.value),
    );
    setActiveIndex(idx >= 0 ? idx : 0);
    setMode("edit");
    setExpanded(false);
    setOpen(true);
  }

  function openHelp() {
    placePopover();
    setMode("help");
    setExpanded(false);
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setExpanded(false);
  }

  function pickChoice(value: string) {
    close();
    if (value !== chip.value) chip.onChange?.(value);
  }

  useEffect(() => {
    if (!open) return;
    function onMove() {
      placePopover();
    }
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);

  useEffect(() => {
    if (!open || mode !== "edit" || !hasChoices) return;
    optionRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [open, mode, hasChoices, activeIndex]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      if (mode !== "edit" || !hasChoices || choices.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % choices.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + choices.length) % choices.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const opt = choices[activeIndex];
        if (opt) pickChoice(opt.value);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, mode, hasChoices, choices, activeIndex]);

  function onLabelClick() {
    if (open) {
      close();
      return;
    }
    if (canEdit) openEdit();
    else if (chip.help) openHelp();
  }

  const avatars = chip.avatars?.filter((a) => a.label.trim()) ?? [];

  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-0.5 rounded-full border border-[var(--color-primary)]/25",
        "bg-[var(--color-primary)]/10 py-0.5 pr-0.5 text-xs font-medium text-[var(--color-primary)]",
        avatars.length > 0 ? "pl-1" : "pl-2.5",
      )}
    >
      {avatars.length > 0 ? (
        <span
          className="flex shrink-0 items-center -space-x-1.5"
          aria-hidden={avatars.length === 1 ? undefined : true}
        >
          {avatars.slice(0, 3).map((a, i) => (
            <PersonAvatar
              key={`${a.label}-${i}`}
              label={a.label}
              src={a.src}
              size="xs"
              variant="soft"
            />
          ))}
        </span>
      ) : null}
      {canOpen ? (
        <button
          ref={labelRef}
          type="button"
          className={cn(
            "truncate py-0.5 pr-0.5 text-left",
            "rounded-sm underline-offset-2 hover:underline",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]",
          )}
          title={canEdit ? `Change: ${chip.label}` : `About: ${help.title}`}
          aria-expanded={open}
          aria-controls={open ? tipId : undefined}
          aria-haspopup={canEdit ? "listbox" : "dialog"}
          onClick={onLabelClick}
        >
          {chip.label}
        </button>
      ) : (
        <span className="truncate py-0.5 pr-0.5">{chip.label}</span>
      )}
      <button
        type="button"
        className={cn(
          "inline-flex size-5 shrink-0 items-center justify-center rounded-full text-[var(--color-primary)]/70",
          "transition-colors hover:bg-[var(--color-primary)]/16 hover:text-[var(--color-primary)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]",
        )}
        title={`Clear ${chip.label}`}
        aria-label={`Clear ${chip.label}`}
        onClick={(e) => {
          e.stopPropagation();
          close();
          chip.onClear();
        }}
      >
        <span aria-hidden>×</span>
      </button>
      {open &&
        pos &&
        createPortal(
          <>
            <button
              type="button"
              className="fixed inset-0 z-[90] cursor-default bg-transparent"
              aria-label="Dismiss"
              onClick={close}
            />
            <div
              id={tipId}
              role={
                mode === "edit" && (hasChoices || hasEditPanel)
                  ? hasChoices
                    ? "listbox"
                    : "dialog"
                  : "dialog"
              }
              aria-label={
                mode === "edit" ? `Change ${help.title}` : help.title
              }
              className="fixed z-[91] w-72 max-w-[calc(100vw-1rem)] rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-card)] p-2 text-left shadow-[var(--shadow-float)]"
              style={{ top: pos.top, left: pos.left }}
              onMouseDown={(e) => {
                // Keep focus/search typing from dismissing via overlay quirks.
                e.stopPropagation();
              }}
            >
              {mode === "edit" && hasEditPanel && !hasChoices ? (
                <>
                  {chip.editPanel!(close)}
                  {chip.help ? (
                    <div className="border-t border-[var(--color-border)]/80 px-2 pt-1.5">
                      <button
                        type="button"
                        className="text-xs font-medium text-[var(--color-accent)] underline-offset-2 hover:underline"
                        onClick={() => {
                          setMode("help");
                          setExpanded(false);
                        }}
                      >
                        About this filter
                      </button>
                    </div>
                  ) : null}
                </>
              ) : mode === "edit" && hasChoices ? (
                <>
                  <div className="px-2 pb-1.5 pt-1 font-[family-name:var(--font-display)] text-xs font-semibold tracking-tight text-[var(--color-muted)]">
                    Change filter
                  </div>
                  <ul className="m-0 max-h-64 list-none space-y-0.5 overflow-y-auto p-0">
                    {choices.map((opt, i) => {
                      const selected = opt.value === chip.value;
                      const active = i === activeIndex;
                      return (
                        <li key={opt.value} className="m-0">
                          <button
                            ref={(el) => {
                              optionRefs.current[i] = el;
                            }}
                            type="button"
                            role="option"
                            aria-selected={selected}
                            className={cn(
                              "flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm",
                              "transition-colors hover:bg-[var(--color-primary)]/8",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]",
                              selected &&
                                "bg-[var(--color-primary)]/12 font-medium text-[var(--color-primary)]",
                              active &&
                                !selected &&
                                "bg-[var(--color-border)]/40",
                              active && "ring-2 ring-[var(--color-ring)]",
                            )}
                            onMouseEnter={() => setActiveIndex(i)}
                            onClick={() => pickChoice(opt.value)}
                          >
                            <span>{opt.label}</span>
                            {selected ? (
                              <span className="text-xs opacity-70" aria-hidden>
                                ✓
                              </span>
                            ) : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  {chip.help ? (
                    <div className="mt-1 border-t border-[var(--color-border)]/80 px-2 pt-1.5">
                      <button
                        type="button"
                        className="text-xs font-medium text-[var(--color-accent)] underline-offset-2 hover:underline"
                        onClick={() => {
                          setMode("help");
                          setExpanded(false);
                        }}
                      >
                        About this filter
                      </button>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="p-1">
                  <div className="font-[family-name:var(--font-display)] text-sm font-semibold tracking-tight text-[var(--color-fg)]">
                    {help.title}
                  </div>
                  <p className="mt-1.5 m-0 text-xs leading-relaxed text-[var(--color-fg)]">
                    {help.summary}
                  </p>
                  {help.example ? (
                    <p className="mt-2 m-0 text-xs leading-relaxed text-[var(--color-muted)]">
                      <span className="font-medium text-[var(--color-fg)]">
                        Example:{" "}
                      </span>
                      {help.example}
                    </p>
                  ) : null}
                  {expanded && hasMore ? (
                    <div className="mt-2 space-y-2 border-t border-[var(--color-border)]/80 pt-2 text-xs leading-relaxed text-[var(--color-muted)]">
                      {help.detail ? <p className="m-0">{help.detail}</p> : null}
                      {help.useCases && help.useCases.length > 0 ? (
                        <ul className="m-0 list-disc space-y-1 pl-4">
                          {help.useCases.map((u) => (
                            <li key={u}>
                              <span className="font-medium text-[var(--color-fg)]">
                                Use when:{" "}
                              </span>
                              {u}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                    {hasMore && !expanded ? (
                      <button
                        type="button"
                        className="text-xs font-medium text-[var(--color-accent)] underline-offset-2 hover:underline"
                        onClick={() => setExpanded(true)}
                      >
                        Show more
                      </button>
                    ) : null}
                    {chip.helpLink ? (
                      <Link
                        to={chip.helpLink.to}
                        className="text-xs font-medium text-[var(--color-accent)] underline-offset-2 hover:underline"
                        onClick={close}
                      >
                        {chip.helpLink.label}
                      </Link>
                    ) : null}
                    {hasChoices || hasEditPanel ? (
                      <button
                        type="button"
                        className="text-xs font-medium text-[var(--color-accent)] underline-offset-2 hover:underline"
                        onClick={() => setMode("edit")}
                      >
                        Change value
                      </button>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          </>,
          document.body,
        )}
    </span>
  );
}
